/**
 * Canopy Units — editor-side helpers for the `units` tag on `number` and `vec3`
 * property declarations.
 *
 * The trace-viewer runtime owns the conversion (`src/core/canopy/units.ts`:
 * `toCanonical`/`fromCanonical`, applied at the resolver's `getValue` read
 * boundary). That module is not reachable through any `trace-viewer/*` subpath
 * export, and the editor needs none of the arithmetic — only the display
 * suffixes and the picker options — so the three-entry table is mirrored here
 * rather than widening trace-viewer's public surface.
 *
 * The `Units` union itself IS derived from the exported `PropertyDeclaration`,
 * so adding a unit in trace-viewer is a type error here rather than silent drift.
 *
 * See trace-viewer `docs/canopy/CANOPY_UNITS.md` §7.2 (editors).
 */

import type { PropertyDeclaration } from 'trace-viewer/behaviors';

/** `'Meters' | 'Feet'` — derived from the viewer's NumberDeclaration. */
export type PropertyUnits = NonNullable<Extract<PropertyDeclaration, { type: 'number' }>['units']>;

/** Short display suffix per unit. Mirrors trace-viewer `UNIT_SUFFIX`. */
export const UNIT_SUFFIX: Record<PropertyUnits, string> = {
    Meters: 'm',
    Feet: 'ft',
};

/** Picker options, in menu order. `''` is "no units" (the `units` key is absent). */
export const UNIT_OPTIONS: ReadonlyArray<{ value: '' | PropertyUnits; label: string; title: string }> = [
    { value: '', label: '—', title: 'No units — values are canonical metres' },
    { value: 'Meters', label: 'm', title: 'Metres' },
    { value: 'Feet', label: 'ft', title: 'Feet — values are authored in feet; the scene reads metres' },
];

/** Only `number` and `vec3` declarations carry length units (CANOPY_UNITS §2). */
export function supportsUnits(type: PropertyDeclaration['type']): boolean {
    return type === 'number' || type === 'vec3';
}

/**
 * The units tag governing a declaration's values. A stray `units` on any other
 * declaration type is ignored, matching the runtime's `declarationUnits`.
 */
export function declarationUnits(decl: PropertyDeclaration | undefined): PropertyUnits | undefined {
    if (!decl || !supportsUnits(decl.type)) return undefined;
    return (decl as PropertyDeclaration & { units?: PropertyUnits }).units;
}

/** Display suffix for a units tag, or `undefined` when there is nothing to show. */
export function unitSuffix(units: PropertyUnits | undefined): string | undefined {
    return units === undefined ? undefined : UNIT_SUFFIX[units];
}

/**
 * Set or clear a declaration's `units`, returning a new declaration.
 *
 * Re-labelling only: `value`, `min`, `max`, `step` (and, by construction, any
 * state override values keyed on this property) are returned untouched.
 * CANOPY_UNITS §7.2 — the ESPN asset was hand-tagged `Feet` precisely because
 * its numbers were already feet, so rescaling on tag would corrupt it.
 *
 * `undefined` deletes the key rather than writing `units: undefined`, so the
 * declaration serializes exactly as it did before it was ever tagged.
 */
export function applyUnits(decl: PropertyDeclaration, units: PropertyUnits | undefined): PropertyDeclaration {
    if (!supportsUnits(decl.type)) return decl;
    const { units: _dropped, ...rest } = decl as PropertyDeclaration & { units?: PropertyUnits };
    return (units === undefined ? rest : { ...rest, units }) as PropertyDeclaration;
}
